import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { DocumentSequenceForm, DocumentSequenceFilter } from '../../types/document/DocumentSequenceTypes';
import DocumentSequenceService from '../../services/document/DocumentSequenceService';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import {
    clearFilteredDocumentSequences,
    clearForm,
    clearFormErrors,
    closeModalAndResetForm,
    closeViewModalAndResetDetail,
    setActiveList,
    setDetail,
    setForm,
    setIsSubmitting,
    setList,
    setShowEditModal,
    setShowRegistrationModal,
    setShowViewModal,
    updateFormField,
    updateSequenceInList,
} from '../../store/slices/document/documentSequenceSlice';
import useModals from '../useModals';
import useApiErrorHandler from '../useApiErrorHandler';


export const useDocumentSequence = () => {
    const dispatch: AppDispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();

    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        form,
        detail,
        list,
        activeList,
        paginator,
        isSubmitting,
        formErrors,
        showRegistrationModal,
        showViewModal,
        showEditModal,
    } = useSelector(
        (state: RootState) => state.documentSequence
    );

    /**
 * Crea una nueva secuencia documental.
 */
    const handlerCreateSequence = async (data: DocumentSequenceForm) => {
        console.log(data);
        try {
            dispatch(setIsSubmitting(true));
            showLoadingIndicator();

            const response = await DocumentSequenceService.create(data);

            const { statusCode, title, message, type } = response;

            if (statusCode === 201) {
                openModal(type, { title, message });
                dispatch(clearForm());
                dispatch(setShowRegistrationModal(false));
            } else {
                openModal(type, { title, message });
            }
        } catch (error: any) {
            handle(error); // 🛑 Errores como red, validaciones backend, etc.
        } finally {
            dispatch(setIsSubmitting(false));
            hideLoadingIndicator();
        }
    };

    const handlerUpdateCurrentNumber = async (id: number, newNumber: number) => {
        try {
            dispatch(setIsSubmitting(true));
            showLoadingIndicator();

            const response = await DocumentSequenceService.updateCurrentNumber(id, newNumber);
            const { type, title, message, data } = response;
            dispatch(updateSequenceInList(data));

            openModal(type, { title, message });
            closeEditSequenceModal();
        } catch (error) {
            handle(error);
        } finally {
            dispatch(setIsSubmitting(false));
            hideLoadingIndicator();
        }
    };



    const handlerGetById = async (id: number) => {
        try {
            showLoadingIndicator();
            const response = await DocumentSequenceService.getById(id);
            console.log(response);

            if (response.statusCode === 200) {
                dispatch(setDetail(response.data));         // ✅ Guarda el detalle
                dispatch(setShowViewModal(true));           // ✅ Abre el modal de vista
            } else {
                showAlert('error', 'Error', 'No se pudo obtener la secuencia.');
            }
        } catch (error) {
            showAlert('error', 'Error', 'No se pudo obtener la secuencia.');
        } finally {
            hideLoadingIndicator();
        }
    };

    // Dentro de useDocumentSequence()

    const handlerOpenEditModal = async (id: number) => {
        try {
            showLoadingIndicator();
            const response = await DocumentSequenceService.getById(id);

            if (response.statusCode === 200) {
                dispatch(setDetail(response.data));      // ✅ Guardar el detalle
                dispatch(setShowEditModal(true));        // ✅ Abrir modal de edición
            } else {
                showAlert('error', 'Error', 'No se pudo obtener la secuencia.');
            }
        } catch (e) {
            handle(e);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
 * Filtra y pagina los números de control (secuencias documentales internas).
 */
    const handlerGetFilteredSequences = async (
        filters: DocumentSequenceFilter,
        page = 0,
        size = 10,
        sort = 'createdAt,desc' // ✅ usar un campo existente, por ejemplo createdAt
    ) => {
        try {
            showLoadingIndicator();
            const response = await DocumentSequenceService.getFiltered(filters, page, size, sort);
            dispatch(setList({ content: response.content, paginator: response }));
        } catch (error) {
            showAlert('error', 'Error', 'No se pudieron obtener los números de control.');
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerGetActiveSequences = async () => {
        try {
            showLoadingIndicator();
            const response = await DocumentSequenceService.getActiveSequences();
            dispatch(setActiveList(response.data));
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Actualiza un campo del formulario.
     */
    const updateField = <K extends keyof DocumentSequenceForm>(
        field: K,
        value: DocumentSequenceForm[K]
    ) => {
        dispatch(updateFormField({ field, value }));
    };

    const handlerClearFilteredDocumentSequences = () => {
        dispatch(clearFilteredDocumentSequences());
    };


    const closeAddSequenceModal = () => {
        dispatch(closeModalAndResetForm());
    };

    const closeEditSequenceModal = () => {
        dispatch(setShowEditModal(false));
    };


    return {
        form,
        detail,
        list,
        activeList,
        paginator,
        formErrors,
        isSubmitting,
        showRegistrationModal,
        showViewModal,

        updateField,
        setForm: (data: DocumentSequenceForm) => dispatch(setForm(data)),
        clearForm: () => dispatch(clearForm()),
        clearErrors: () => dispatch(clearFormErrors()),

        openAddSequenceModal: () => dispatch(setShowRegistrationModal(true)),

        closeViewModal: () => {
            dispatch(closeViewModalAndResetDetail());
        },

        handlerCreateSequence,
        handlerGetById,
        handlerGetFilteredSequences,
        handlerGetActiveSequences,
        handlerClearFilteredDocumentSequences,
        closeAddSequenceModal,

        showEditModal,
        handlerOpenEditModal,
        closeEditSequenceModal,
        handlerUpdateCurrentNumber,
    };
};
